import { useEffect, useRef } from 'react'
import { Plus } from 'lucide-react'
import type { AssistantTurn, ChatTurn, Dashboard } from '../types'
import { ChatTurnView } from './ChatTurn'
import { InfoTip } from './InfoTip'

const EXAMPLES = [
  'Show the top 8 brands by CU sold this year.',
  'Which brands are most popular in Malmö compared to Stockholm this year?',
  'Show packages sold per month this year.',
  'Compare monthly CU sold for ZYN and VELO this year, using two clearly distinguishable lines.',
  'Show the top 10 cities by packages sold in the last 30 days.',
]

// The composer is the docked AskBar (App owns it, bottom of the workspace). This
// view renders the thread / empty hero only.
export function ChatView({
  turns,
  onAsk,
  onRetry,
  onNewChat,
  dashboards,
  pinnedTurnIds,
  onPinTo,
  onPinDragStart,
  onPinDragEnd,
  preferredDashboardId,
  onOpenDashboard,
  internal = false,
}: {
  turns: ChatTurn[]
  onAsk: (q: string) => void
  onRetry: (turn: AssistantTurn) => void
  onNewChat: () => void
  dashboards: Dashboard[]
  pinnedTurnIds: Set<string>
  onPinTo: (t: AssistantTurn, target: string | 'new') => void
  onPinDragStart: (t: AssistantTurn) => void
  onPinDragEnd: () => void
  preferredDashboardId?: string | null
  onOpenDashboard?: (id: string) => void
  internal?: boolean
}) {
  const bottom = useRef<HTMLDivElement | null>(null)
  const thread = useRef<HTMLDivElement | null>(null)
  const stick = useRef(true) // auto-scroll only while the user is at the bottom
  useEffect(() => {
    const el = thread.current
    if (!el) return
    const onScroll = () => {
      stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [turns.length > 0])
  useEffect(() => {
    // Don't yank a user who scrolled UP to read an earlier answer — only follow new
    // deltas when they're already pinned to the bottom.
    if (stick.current) bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turns])

  if (turns.length === 0) {
    return (
      <div className="chat-empty" key="empty">
        <div className="hero">
          <h1>Ask your sales data.</h1>
          <p>
            Ask a question in plain language.
            <InfoTip label="About the chat" size="small">
              Type your question in the field below — you'll get a clear answer, sometimes with a chart. Like an answer? Pin it to a dashboard you build yourself.
            </InfoTip>
          </p>
          <div className="examples">
            {[...EXAMPLES, ...(internal ? ['Show the number of orders per month this year from Heart.'] : [])].map((ex) => (
              <button key={ex} className="example" type="button" onClick={() => onAsk(ex)}>
                {ex}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="chat-active" key="active">
      <div className="chat-head">
        <span className="chat-head-label">Ask</span>
        {preferredDashboardId && onOpenDashboard && dashboards.some((d) => d.id === preferredDashboardId) && (
          <button className="chat-new" onClick={() => onOpenDashboard(preferredDashboardId)}>
            Back to {dashboards.find((d) => d.id === preferredDashboardId)?.name}
          </button>
        )}
        <button className="chat-new" onClick={onNewChat}>
          <Plus size={14} /> New question
        </button>
      </div>
      <div className="thread" ref={thread}>
        {turns.map((t) => (
          <ChatTurnView
            key={t.id}
            turn={t}
            dashboards={dashboards}
            pinned={pinnedTurnIds.has(t.id)}
            onPinTo={t.role === 'assistant' ? onPinTo : undefined}
            onPinDragStart={t.role === 'assistant' ? onPinDragStart : undefined}
            onPinDragEnd={t.role === 'assistant' ? onPinDragEnd : undefined}
            onRetry={onRetry}
            preferredDashboardId={preferredDashboardId}
          />
        ))}
        <div ref={bottom} />
      </div>
    </div>
  )
}
